import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Margin } from '../types';
import type {
  CoincidentAggregation,
  XyzDataPoint,
  ZInterpolation,
} from '../types';
import {
  collapseCoincident,
  type CollapsedSample,
} from '../data/collapseCoincident';
import { toScalarGrid } from '../data/toScalarGrid';
import type { HorizontalAxes } from '../data/horizontalAxes';
import { toIsolines, toThresholds } from '../geometry/marchingSquares';
import {
  createColorScale,
  type SequentialRampName,
} from '../render/colorScale';
import {
  projectSamples,
  type ProjectedSample,
  DEFAULT_CONTOUR_STYLE,
  renderContour,
  type ContourStyle,
} from '../render/renderContour';
import { renderAxes2d, toMeasuredMargin } from '../render/renderAxes2d';
import {
  SURFACE_PAINT_SIZE,
  toSurfacePaint,
  toUnknownPaintColor,
  type SurfaceColoring,
} from '../render/surfacePaint';
import { tooltipFlipTransform } from './tooltipPlacement';
import { toExportCanvas } from './exportCanvas';
import { useChartCanvas, useContainerSize } from './useChartCanvas';

export type ContourChartProps = {
  points: XyzDataPoint[];
  xTitle: string;
  yTitle: string;
  /**
   * The field the colour encodes, for the accessible name.
   *
   * Required rather than optional: colour is the only channel this chart has for it, so a contour
   * with no name for it is a chart of nothing in particular - and with no legend anywhere, this
   * string is the whole key.
   */
  zTitle: string;
  interpolation: ZInterpolation;
  aggregation: CoincidentAggregation;
  resolution: number;
  /**
   * The two horizontal axes, decided by the host.
   *
   * Required rather than derived from `points`, so the four XYZ views can be made to agree about
   * the plane they share and so the choice sits in the source-data layer with the rest of the
   * derivation. `toDataHorizontalAxes` is the baseline for a caller with no opinion.
   *
   * The range must contain the points' own extent. A narrower one does not clip - it places marks
   * outside the box.
   */
  axes: HorizontalAxes;
  ramp: SequentialRampName;
  bands: number;
  /** What colors the bands. The height, through `ramp` and `bands`, when absent. */
  coloring?: SurfaceColoring;
  contourCount: number;
  style?: Partial<ContourStyle>;
  /**
   * Overrides any of the four gutters. Partial because the defaults are per field, and because the
   * left and right gutters are widened at paint time to fit whatever `format` produces - a caller
   * usually wants to adjust one edge rather than restate all four.
   */
  margin?: Partial<Margin>;
  format: (value: number) => string;
  /**
   * Names the components stacked on one position, for the hover tooltip.
   *
   * Threaded in rather than built here, like `format`: this package has no locale, and the string
   * is the one part of the tooltip that is prose rather than a field name or a number.
   */
  formatCoincidentCount: (count: number) => string;
  /**
   * The whole accessible name, assembled by the host.
   *
   * Threaded in for the same reason `formatCoincidentCount` is - this package has no locale - and
   * it matters more than that one does. With no legend, this sentence is the only place the chart
   * says that colour carries a field, so left in English it is the half of the no-legend story that
   * does not survive translation.
   */
  formatAccessibleName: (parts: AccessibleNameParts) => string;
  /**
   * Publishes the chart's export hook once there is a canvas, and null when it goes.
   *
   * A callback rather than a ref the host fills, so the host cannot hold a hook that outlives the
   * canvas it reads. Optional: the stories and the tests have nothing to export to.
   */
  onExportCanvasAvailable?: (
    createExportCanvas: (() => OffscreenCanvas | null) | null
  ) => void;
  /**
   * The header lines the export has to carry, since the exported file is the canvas alone.
   *
   * Passed in rather than built here: they are the host's own DOM header, translated, and this
   * package has no locale.
   */
  exportCaptions?: readonly string[];
  /**
   * Reports whether there is a chart to look at, which the host cannot work out for itself.
   *
   * The source data can be perfectly plottable and still grid to nothing: a triangulation with
   * area that lays no cell centre inside any of its triangles. In that state this renders
   * `emptyState` and no canvas at all, so an export would fail from an enabled menu entry. Same
   * signal, and the same reason for it, as `Surface3DChart`'s.
   */
  onDrawableChange?: (isDrawable: boolean) => void;
  surfaceColor: string;
  inkColor: string;
  gridColor: string;
  emptyState?: React.ReactNode;
};

/**
 * What a screen reader hears on the chart canvas.
 *
 * Less than the 3D charts need, because there is no keyboard path to explain: this canvas is not
 * focusable and there is nothing to rotate. What it does need is the same as theirs - a role, so
 * it is not an element with no fallback content, and the fields, because "contour chart"
 * identifies nothing when the axes are chosen per view.
 */
type AccessibleNameParts = {
  pointCount: number;
  xTitle: string;
  yTitle: string;
  zTitle: string;
};

const DEFAULT_CONTOUR_MARGIN: Margin = {
  left: 56,
  top: 16,
  right: 16,
  bottom: 44,
};

/**
 * The sample under the cursor, or null.
 *
 * Nearest within a radius rather than the first inside one: markers overlap at this density, and
 * taking the first in array order means the tooltip names whichever component the data happened to
 * list first rather than the one being pointed at.
 */
const HOVER_RADIUS = 14;

const nearestSample = (
  projected: ProjectedSample[],
  cursor: { x: number; y: number }
): CollapsedSample | null => {
  let best: ProjectedSample | null = null;
  let bestDistance = HOVER_RADIUS * HOVER_RADIUS;

  for (const entry of projected) {
    const dx = entry.screenX - cursor.x;
    const dy = entry.screenY - cursor.y;
    const distance = dx * dx + dy * dy;
    if (distance <= bestDistance) {
      best = entry;
      bestDistance = distance;
    }
  }

  return best?.sample ?? null;
};

export const ContourChart = ({
  points,
  xTitle,
  yTitle,
  zTitle,
  interpolation,
  aggregation,
  resolution,
  axes,
  ramp,
  bands,
  coloring,
  contourCount,
  style,
  margin,
  format,
  formatCoincidentCount,
  formatAccessibleName,
  onExportCanvasAvailable,
  exportCaptions,
  onDrawableChange,
  surfaceColor,
  inkColor,
  gridColor,
  emptyState,
}: ContourChartProps) => {
  /* Destructured to numbers, then rebuilt, for the same reason `style` and `margin` are: `axes`
     is a host-built object, and a host that assembles it inline hands over a new one on every
     parent render. Fed straight into `paint`'s dependency list that reads as a change, and every
     such render repaints - which here is the per-device-pixel band loop. */
  const {
    x: {
      range: xAxisRange,
      tickCount: xTickCount,
      padding: [xPaddingLow, xPaddingHigh],
    },
    y: {
      range: yAxisRange,
      tickCount: yTickCount,
      padding: [yPaddingLow, yPaddingHigh],
    },
  } = axes;
  const [xAxisMin, xAxisMax] = xAxisRange;
  const [yAxisMin, yAxisMax] = yAxisRange;
  const resolvedAxes = useMemo<HorizontalAxes>(
    () => ({
      x: {
        range: [xAxisMin, xAxisMax],
        tickCount: xTickCount,
        padding: [xPaddingLow, xPaddingHigh],
      },
      y: {
        range: [yAxisMin, yAxisMax],
        tickCount: yTickCount,
        padding: [yPaddingLow, yPaddingHigh],
      },
    }),
    [
      xAxisMin,
      xAxisMax,
      xTickCount,
      xPaddingLow,
      xPaddingHigh,
      yAxisMin,
      yAxisMax,
      yTickCount,
      yPaddingLow,
      yPaddingHigh,
    ]
  );

  const sampled = useMemo(
    () =>
      toScalarGrid({
        points,
        interpolation,
        aggregation,
        resolution,
        /* Clipped back to the hull in `renderContour`, which is what makes the boundary the
           triangulation's own straight edges rather than the lattice's staircase. */
        overfillForClipping: true,
      }),
    [points, interpolation, aggregation, resolution]
  );

  /* The host's axes folded into the grid, so the bands, the hull clip, the markers, the measured
     gutter and the labels are all read off one pair of ranges. The lattice keeps `dataXRange` and
     `dataYRange`, so a host that widens an axis leaves the field inside the frame rather than
     filling it. */
  const grid = useMemo(
    () => ({
      ...sampled,
      xRange: resolvedAxes.x.range,
      yRange: resolvedAxes.y.range,
    }),
    [sampled, resolvedAxes]
  );

  /* One marker per position, not per component - the same collapse `toScalarGrid` performs
     internally to build the grid these markers are drawn over. Without it a 420-component estate
     on two 1-5 scores paints 420 arcs onto ~25 visible dots, and the marks and the field are
     derived from different populations even where they happen to coincide. */
  const samples = useMemo(
    () => collapseCoincident(points, aggregation),
    [points, aggregation]
  );

  const isolines = useMemo(
    () =>
      contourCount > 0
        ? toIsolines(grid, toThresholds(grid.zRange, contourCount))
        : [],
    [grid, contourCount]
  );

  /* Destructured to fields, then memoised on them. Both feed `paint`'s dependency list, and
     `useChartCanvas` reschedules a full repaint whenever `paint` changes identity - so a host
     writing `style={{ ... }}` or `margin={{ ... }}` inline repainted on every parent render,
     which here is the per-device-pixel band loop. Same gate the controller got as `isSameInputs`;
     these two charts never had one. */
  const {
    showFilledBands = DEFAULT_CONTOUR_STYLE.showFilledBands,
    showIsolines = DEFAULT_CONTOUR_STYLE.showIsolines,
    showSamplePositions = DEFAULT_CONTOUR_STYLE.showSamplePositions,
    opacity = DEFAULT_CONTOUR_STYLE.opacity,
  } = style ?? {};

  const resolvedStyle = useMemo(
    () => ({ showFilledBands, showIsolines, showSamplePositions, opacity }),
    [showFilledBands, showIsolines, showSamplePositions, opacity]
  );

  const {
    left = DEFAULT_CONTOUR_MARGIN.left,
    top = DEFAULT_CONTOUR_MARGIN.top,
    right = DEFAULT_CONTOUR_MARGIN.right,
    bottom = DEFAULT_CONTOUR_MARGIN.bottom,
  } = margin ?? {};

  /* Held across paints rather than built inside one, matching the other two charts. This paint has
     no camera in its dependency list, so it is not rebuilt per frame of a gesture and the saving
     is smaller - but a `ColorScale` carries a closure and a band-edge array, and there is no
     reason for this to be the one chart that reallocates them. */
  const colorScale = useMemo(
    () => createColorScale({ range: grid.zRange, ramp, bands }),
    [grid.zRange, ramp, bands]
  );

  const bandPaint = useMemo(
    () =>
      coloring
        ? toSurfacePaint({
            coloring,
            points,
            grid,
            interpolation,
            resolution,
            overfillForClipping: true,
            unknownColor: toUnknownPaintColor(surfaceColor),
            size: SURFACE_PAINT_SIZE,
          })
        : null,
    [coloring, points, grid, interpolation, resolution, surfaceColor]
  );

  const resolvedMargin = useMemo(
    () => ({ left, top, right, bottom }),
    [left, top, right, bottom]
  );

  const paint = useCallback(
    (
      context: CanvasRenderingContext2D,
      viewport: { width: number; height: number }
    ) => {
      if (grid.width < 2) return;

      /* Widened here rather than declared, because `format` belongs to the host and the ranges are
         the field's own units - the same chart carries `0.4` on one viewpoint and `1,250,000` on
         the next, and at a fixed gutter the second overlapped the axis title and then the canvas
         edge. */
      const axisMargin = toMeasuredMargin({
        context,
        margin: resolvedMargin,
        xRange: grid.xRange,
        yRange: grid.yRange,
        format,
        xTickCount,
        yTickCount,
        xPadding: resolvedAxes.x.padding,
        yPadding: resolvedAxes.y.padding,
      });

      /* Recorded here rather than recomputed on each pointer event: `axisMargin` is measured
         against this context's font metrics, so it is only correct inside the paint that measured
         it.

         Empty when the markers are hidden, so the hit test targets what is drawn. Left ungated, an
         apparently blank field answered a hover with a tooltip for a marker deliberately not
         shown. */
      projected.current = resolvedStyle.showSamplePositions
        ? projectSamples(grid, samples, viewport, axisMargin)
        : [];

      renderContour({
        context,
        grid,
        isolines,
        samples,
        viewport,
        margin: axisMargin,
        colorScale,
        paint: bandPaint,
        style: resolvedStyle,
        inkColor,
        surfaceColor,
      });

      renderAxes2d({
        context,
        viewport,
        margin: axisMargin,
        xTitle,
        yTitle,
        xRange: grid.xRange,
        yRange: grid.yRange,
        format,
        gridColor,
        inkColor,
        xTickCount,
        yTickCount,
        xPadding: resolvedAxes.x.padding,
        yPadding: resolvedAxes.y.padding,
      });
    },
    [
      grid,
      xTickCount,
      yTickCount,
      resolvedAxes,
      isolines,
      samples,
      resolvedMargin,
      resolvedStyle,
      colorScale,
      bandPaint,
      xTitle,
      yTitle,
      format,
      gridColor,
      inkColor,
      surfaceColor,
    ]
  );

  /* The contour chart is flat and has no orbit, so it owns the ref only to satisfy the shared
     canvas hook. */
  const projected = useRef<ProjectedSample[]>([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { containerRef, viewport } = useContainerSize();
  const { hasPainted } = useChartCanvas(paint, canvasRef, viewport);

  /* Published once, through a ref so the identity is stable: the hook reads `canvasRef` at call
     time, so it stays correct across repaints and resizes without the host re-registering. Cleared
     on unmount, so a stale hook cannot outlive the canvas. */
  const onExportCanvasAvailableRef = useRef(onExportCanvasAvailable);
  onExportCanvasAvailableRef.current = onExportCanvasAvailable;

  /* Read through a ref for the same reason the hook is published through one: the captions change
     with the field selection, and re-publishing the hook on each change would have the host
     re-register the whole view pane. */
  const exportCaptionsRef = useRef(exportCaptions);
  exportCaptionsRef.current = exportCaptions;

  useEffect(() => {
    const publish = onExportCanvasAvailableRef.current;
    publish?.(() =>
      /* Nothing painted is nothing to export, and it is not something the copy can detect: an
         unpainted canvas carries the HTML default 300x150 backing store, so it would come back as
         a blank PNG of that size rather than as the missing canvas `pngExporter` reports. */
      hasPainted()
        ? toExportCanvas({
            canvas: canvasRef.current,
            backgroundColor: surfaceColor,
            inkColor,
            captions: exportCaptionsRef.current ?? [],
          })
        : null
    );
    return () => publish?.(null);
  }, [surfaceColor, inkColor, hasPainted]);

  const [hovered, setHovered] = useState<CollapsedSample | null>(null);
  const [tooltipAt, setTooltipAt] = useState<{ x: number; y: number } | null>(
    null
  );

  /* Dropped when the data changes, and when the grid does. A hover naming a component that is no
     longer plotted would survive until the pointer moved, and a filter applied elsewhere moves no
     pointer.

     `grid` as well as `samples` because `interpolation` can empty the grid without touching the
     samples - a collinear estate fills nothing under `linear` and fills fine under `smooth` - and
     the canvas that owns `pointerleave` unmounts with it. Without this the hover outlives the thing
     it was pointing at. */
  useEffect(() => {
    setHovered(null);
    setTooltipAt(null);
  }, [samples, grid]);

  /* Reported rather than derived by the host: `grid.width < 2` is what decides whether there is a
     canvas at all, and reaching it needs the gridding this chart has already done. Through a ref
     so that a host passing an inline callback does not re-notify on every render of its own. */
  const onDrawableChangeRef = useRef(onDrawableChange);
  onDrawableChangeRef.current = onDrawableChange;

  const isDrawable = grid.width >= 2;

  useEffect(() => {
    onDrawableChangeRef.current?.(isDrawable);
  }, [isDrawable]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        background: surfaceColor,
        /* So the tooltip's absolute offsets are canvas coordinates rather than the page's. */
        position: 'relative',
      }}
    >
      {!isDrawable ? (
        emptyState
      ) : (
        <>
          <canvas
            ref={canvasRef}
            role="img"
            aria-label={formatAccessibleName({
              pointCount: points.length,
              xTitle,
              yTitle,
              zTitle,
            })}
            style={{ display: 'block' }}
            onPointerMove={event => {
              const rect = event.currentTarget.getBoundingClientRect();
              const cursor = {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
              };
              setTooltipAt(cursor);
              setHovered(nearestSample(projected.current, cursor));
            }}
            onPointerLeave={() => {
              setHovered(null);
              setTooltipAt(null);
            }}
          />
          {hovered && tooltipAt && (
            <div
              style={{
                position: 'absolute',
                left: tooltipAt.x + 14,
                top: tooltipAt.y + 14,
                maxWidth: 'min(320px, 60%)',
                /* The container clips, and the markers reach the hull - so the rightmost sample sits
                 about a gutter's width from the edge and its tooltip would be cut to its longest
                 word. Flipped off whichever edge it would cross. */
                transform: tooltipFlipTransform({ at: tooltipAt, viewport }),
                padding: '6px 10px',
                borderRadius: 4,
                background: surfaceColor,
                color: inkColor,
                border: `1px solid ${gridColor}`,
                font: '12px system-ui, -apple-system, "Segoe UI", sans-serif',
                pointerEvents: 'none',
              }}
            >
              <strong>
                {hovered.count === 1
                  ? hovered.points[0].label
                  : formatCoincidentCount(hovered.count)}
              </strong>
              <div>
                {xTitle}: {format(hovered.x)}
              </div>
              <div>
                {yTitle}: {format(hovered.y)}
              </div>
              <div>
                {zTitle}: {format(hovered.z)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
